import fp from 'lodash/fp';
import { isValid, format } from 'date-fns';
import { ru } from 'date-fns/locale';


type AvailableLocales = 'en' | 'ru';

let currentLocale: AvailableLocales = 'en';
export const setLocale = (locale: AvailableLocales) => {
    currentLocale = locale;
};

const padZero = (number: number, length: number) => `${number}`.padStart(length, '0');

const dateFormatters = {
    en: {
        date: (date: Date) => `${
            date.getMonth() + 1}/${
            padZero(date.getDate(), 2)}/${
            date.getFullYear()
        }`,
        time: (date: Date) => `${
            padZero(date.getHours() % 12 || 12, 2)}:${
            padZero(date.getMinutes(), 2)} ${
            date.getHours() >= 12 ? 'PM' : 'AM'
        }`,
        timeSeconds: (date: Date) => `${
            padZero(date.getHours() % 12 || 12, 2)}:${
            padZero(date.getMinutes(), 2)}:${
            padZero(date.getSeconds(), 2)} ${
            date.getHours() >= 12 ? 'PM' : 'AM'
        }`,
        dateTime: (date: Date) => `${
            dateFormatters.en.date(date)}, ${
            dateFormatters.en.time(date)
        }`,
    },
    ru: {
        date: (date: Date) => `${
            padZero(date.getDate(), 2)}.${
            padZero(date.getMonth() + 1, 2)}.${
            date.getFullYear()
        }`,
        time: (date: Date) => `${
            padZero(date.getHours(), 2)}:${
            padZero(date.getMinutes(), 2)}`,
        timeSeconds: (date: Date) => `${
            padZero(date.getHours(), 2)}:${
            padZero(date.getMinutes(), 2)}:${
            padZero(date.getSeconds(), 2)}`,
        dateTime: (date: Date, divider = ' ') => `${
            dateFormatters.ru.date(date)}${divider}${
            dateFormatters.ru.time(date)
        }`,
    },
};

const createDate = (day: number, month: number, year: number) => {
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
};

const dateParsers = {
    en: (value: string) => {
        const [month, day, year] = value.split('/').map(part => Number(part.trim()));
        return createDate(day, month, year);
    },
    ru: (value: string) => {
        const [day, month, year] = value.split('.').map(part => Number(part.trim()));
        return createDate(day, month, year);
    },
};

export const castDate = (date: string | Date): Date | null => {
    if (!date)
        return null;

    const castDate = fp.isDate(date) ? date : new Date(date);
    if (Number.isNaN(castDate.getTime()))
        return null;

    return castDate;
};

type DateFormatter = (date: Date | string, locale?: AvailableLocales, divider?: string) => string;
const dateFormatter = (type: keyof typeof dateFormatters.en): DateFormatter =>
    (date: Date | string, locale: AvailableLocales = currentLocale, divider = ' ') => fp.pipe(
        castDate,
        (date: Date | null): string => date
            ? dateFormatters[locale][type](date, divider)
            : '',
    )(date);

export const formatDate = dateFormatter('date');
export const formatTime = dateFormatter('time');
export const formatTimeSeconds = dateFormatter('timeSeconds');
export const formatDateTime = dateFormatter('dateTime');

export const formatDateOnlyForBackend = (date: Date) => date
    ? `${date.getFullYear()}-${padZero(date.getMonth() + 1, 2)}-${padZero(date.getDate(), 2)}`
    : '';

export const parseFloatToFixed = (value: number, fixed: number = 2): string =>
    Number.isFinite(value)
        ? parseFloat(value.toString()).toFixed(fixed)
        : undefined;

export const formatCurrency = (value: number | string, unit?: string, fixed: number = 2) =>
    `${(typeof value === 'string' ? value : Number.isFinite(value) ? parseFloatToFixed(value, fixed) : '')
        .replace(/(\d)(?=(\d{3})+(\.|$))/g, '$1 ')
        .replace('.', ',')}${unit ? ` ${unit}` : ''}`;

export const getOr = fp.curry((data: any, path: string) => {
    const value = fp.getOr('', path, data);
    return fp.defaultTo('', value);
});

export const getDateOr = fp.curry(
    (data: any, path: string) => formatDate(getOr(data, path)),
);

export const formatOr = <TData, TResult>(
    data: TData,
    formatter: (data: TData) => TResult,
    defaultValue?: TResult,
) => data || Number.isFinite(data) ? formatter(data) : defaultValue;

export const parseDate = (
    value: string,
    locale: AvailableLocales = currentLocale,
) => dateParsers[locale](value);

export const getFormattedDate = (dateString: string, formatType: string) => {
    const date = new Date(dateString);
    return isValid(date)
        ? format(date, formatType, { locale: ru })
        : undefined;
};

type OptionsConvertedDate = 'UTCFormat' | 'withTime';

export const getConvertedDate = (option: OptionsConvertedDate, date?: Date) => {
    if (!date)
        return date;

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    if (option === 'withTime') {
        const hours = date.getHours();
        const minutes = date.getMinutes();

        return new Date(year, month, day, hours, minutes);
    }

    return new Date(Date.UTC(year, month, day));
};
